import { useEffect, useState } from 'react';
import { helpdeskApi, type HelpdeskTicketDto } from '../api/helpdesk';
import { HD_STATUS } from './status';
import { ticketCodeLabel } from './ticket-code';
import { ArcPanel } from '../components/arc/primitives/arc-primitives';
import './helpdesk-embed.css';

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('cs-CZ');
}

function uniqueEmails(emails: ReadonlyArray<string | null | undefined>): string[] {
  const seen = new Set<string>();
  for (const e of emails) {
    const clean = e?.trim().toLowerCase();
    if (clean) seen.add(clean);
  }
  return [...seen].sort();
}

export function HelpdeskContactTickets({ emails }: { emails: ReadonlyArray<string | null | undefined> | null }) {
  const [tickets, setTickets] = useState<HelpdeskTicketDto[] | null>(null);
  const [denied, setDenied] = useState(false);
  const list = emails ? uniqueEmails(emails) : null;
  const key = list?.join(',') ?? null;

  useEffect(() => {
    if (key === null) { setTickets(null); return; }
    const targets = key ? key.split(',') : [];
    if (targets.length === 0) { setTickets([]); return; }
    let alive = true;
    setTickets(null);
    Promise.all(targets.map((e) => helpdeskApi.ticketsByEmail(e)))
      .then((results) => {
        if (!alive) return;
        const byId = new Map<string, HelpdeskTicketDto>();
        for (const r of results) for (const t of r.tickets) byId.set(t.id, t);
        const merged = [...byId.values()].sort((a, b) =>
          (b.lastMessageAt ?? b.createdAt).localeCompare(a.lastMessageAt ?? a.createdAt));
        setTickets(merged);
      })
      .catch(() => { if (alive) setDenied(true); });
    return () => { alive = false; };
  }, [key]);

  if (denied) return <div className="hde-empty">Nemáte přístup do Helpdesku.</div>;
  if (list !== null && list.length === 0) {
    return <div className="prototype-empty-contact">Žádná kontaktní osoba nemá e-mail, tickety nejde přiřadit.</div>;
  }

  return (
    <ArcPanel as="div" className="hde-card">
      <div className="hde-title">Helpdesk tickety {tickets ? `(${tickets.length})` : ''}</div>
      {tickets === null && <div className="hde-empty">Načítám…</div>}
      {tickets && tickets.length === 0 && <div className="hde-empty">Od kontaktních osob zatím žádný ticket nepřišel.</div>}
      {tickets && tickets.length > 0 && (
        <ul className="hde-list">
          {tickets.map((t) => {
            const st = HD_STATUS[t.status] ?? { label: t.status, color: '#6c6c70' };
            return (
              <li key={t.id}>
                <a className="hdc-row" href={`/helpdesk?ticketId=${encodeURIComponent(t.id)}`}>
                  <span className="hdc-date">{fmtDate(t.lastMessageAt ?? t.createdAt)}</span>
                  <span className="hdc-name">{t.contactFullName || t.fromName || '-'}</span>
                  <span className="hdc-email">{t.fromEmail}</span>
                  <span className="hdc-subject">{t.subject}</span>
                  <span className="hdc-code">{ticketCodeLabel(t)}</span>
                  <span className="hde-badge" style={{ color: st.color, background: `${st.color}1f` }}>{st.label}</span>
                  <span className="hdc-operator">{t.assigneeUserName ?? '-'}</span>
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </ArcPanel>
  );
}
